import { Service, signal } from '@angular/core';
import { STORAGE_KEYS } from './constants';

/** Table UUID must be a real GUID — the socket server rejects non-UUID topics. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Query param the QR encodes the table GUID under (value is used as the API tableUuid). */
const TABLE_QUERY_PARAM = 'tableId';

@Service()
export class QrTokenService {
  private readonly key = STORAGE_KEYS.QR_TOKEN;
  private readonly tableKey = STORAGE_KEYS.TABLE_UUID;

  private readonly _token = signal<string | null>(null);
  readonly token = this._token.asReadonly();

  private readonly _tableUuid = signal<string | null>(null);
  /** Table GUID (from `?tableId=`), scopes table-service calls; null when the QR carries no table. */
  readonly tableUuid = this._tableUuid.asReadonly();

  /**
   * Reads the token (first path segment) and the table GUID (`?tableId=`) from the URL,
   * persists both, then strips them from the address bar in a single history rewrite.
   */
  initFromUrl(): void {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const params = new URLSearchParams(window.location.search);

    let tokenSegmentConsumed = false;
    const rawToken = segments[0];
    if (rawToken) {
      try {
        const token = decodeURIComponent(rawToken).trim();
        if (token) {
          this.setToken(token);
          tokenSegmentConsumed = true;
        } else {
          // Whitespace-only segment — treat it as absent, fall back to storage.
          this.restore();
        }
      } catch {
        // Malformed segment (e.g. '%zz') — leave it in the URL, fall back to storage.
        this.restore();
      }
    } else {
      this.restore();
    }

    let tableParamConsumed = false;
    const rawTable = params.get(TABLE_QUERY_PARAM);
    if (UUID_RE.test(rawTable?.trim() ?? '')) {
      this.setTable(rawTable!.trim());
    } else if (tokenSegmentConsumed) {
      // A URL carrying the token is a fresh QR entry — it fully defines the table.
      // No (valid) tableId means no table; a previously stored one must not leak in.
      this.clearTable();
    } else {
      // Reload of an already-stripped URL — restore what the QR entry established.
      this.restoreTable();
    }
    if (rawTable !== null) {
      params.delete(TABLE_QUERY_PARAM);
      tableParamConsumed = true;
    }

    if (tokenSegmentConsumed || tableParamConsumed) {
      const rest = (tokenSegmentConsumed ? segments.slice(1) : segments).join('/');
      const query = params.toString();
      const cleanUrl = '/' + rest + (query ? '?' + query : '') + window.location.hash;
      history.replaceState({}, '', cleanUrl);
    }
  }

  restore(): void {
    const saved = this.read(this.key);
    if (saved) this._token.set(saved);
  }

  restoreTable(): void {
    const saved = this.read(this.tableKey);
    if (saved) this._tableUuid.set(saved);
  }

  setToken(token: string): void {
    const value = token?.trim();
    if (!value) return; // empty/whitespace segment must not reach storage/header
    this.write(this.key, value);
    this._token.set(value);
  }

  setTable(tableUuid: string): void {
    const value = tableUuid?.trim();
    if (!value) return;
    this.write(this.tableKey, value);
    this._tableUuid.set(value);
  }

  clear(): void {
    this.remove(this.key);
    this._token.set(null);
  }

  clearTable(): void {
    this.remove(this.tableKey);
    this._tableUuid.set(null);
  }

  get value(): string | null {
    return this.token();
  }

  get table(): string | null {
    return this.tableUuid();
  }

  private read(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      // sessionStorage unavailable (private mode / blocked) — ignore.
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage unavailable — value still lives in the signal for this session.
    }
  }

  private remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage unavailable — ignore; signal is reset regardless.
    }
  }
}
